import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Check, Flame, Clock, X, Mail, MessageCircle, Lightbulb } from "lucide-react";
import { useCountdown } from "@/hooks/useCountdown";
import footballLogo from "@/assets/football-logo.webp";
import { getDailyFact } from "@/data/footballFacts";

interface AnswerResult {
  questionIndex: number;
  correct: boolean;
}

interface ResultScreenProps {
  score: number;
  total: number;
  streak?: number;
  alreadyPlayed?: boolean;
  onPlayAgain?: () => void;
  results?: AnswerResult[];
}

const QUIZ_URL = "https://dailyfootballquiz.com";

const ResultScreen = ({ score, total, streak = 0, alreadyPlayed, results }: ResultScreenProps) => {
  const [copied, setCopied] = useState(false);
  const [factCopied, setFactCopied] = useState(false);
  const [showPlatforms, setShowPlatforms] = useState(false);
  const { hours, minutes, seconds } = useCountdown();

  const dailyFact = getDailyFact();

  const emoji = score === total ? "🏆" : score >= 3 ? "👏" : "😅";
  const message =
    score === total
      ? "Perfect score!"
      : score >= 3
      ? "Well played!"
      : "Better luck next time!";

  // Use universally-supported emoji that render correctly across all platforms
  const correctEmoji = "✅";
  const wrongEmoji = "❌";

  const scoreBlocks = results && results.length === total
    ? [...results]
        .sort((a, b) => a.questionIndex - b.questionIndex)
        .map((r) => (r.correct ? correctEmoji : wrongEmoji))
        .join("")
    : Array.from({ length: total }, (_, i) => (i < score ? correctEmoji : wrongEmoji)).join("");

  const streakText = streak > 1 ? `\n🔥 ${streak} day streak!` : "";
  const shareText = `⚽ Daily Football Quiz\n${scoreBlocks}\n${score}/${total} ${emoji}${streakText}\nCan you beat my score?\n${QUIZ_URL}`;
  const shareTextEncoded = encodeURIComponent(shareText);
  const quizUrlEncoded = encodeURIComponent(QUIZ_URL);

  const platforms = [
    {
      name: "WhatsApp",
      icon: () => <MessageCircle size={18} />,
      color: "bg-[#25D366] hover:bg-[#1da851]",
      url: `https://wa.me/?text=${shareTextEncoded}`,
    },
    {
      name: "X",
      icon: () => <X size={18} />,
      color: "bg-[#000000] hover:bg-[#333333]",
      url: `https://x.com/intent/tweet?text=${shareTextEncoded}`,
    },
    {
      name: "Facebook",
      icon: () => <span className="font-bold text-sm">f</span>,
      color: "bg-[#1877F2] hover:bg-[#0d65d9]",
      url: `https://www.facebook.com/sharer/sharer.php?u=${quizUrlEncoded}&quote=${shareTextEncoded}`,
    },
    {
      name: "Telegram",
      icon: () => <Share2 size={18} />,
      color: "bg-[#0088cc] hover:bg-[#006daa]",
      url: `https://t.me/share/url?url=${quizUrlEncoded}&text=${shareTextEncoded}`,
    },
    {
      name: "Reddit",
      icon: () => <span className="font-bold text-sm">r/</span>,
      color: "bg-[#FF4500] hover:bg-[#cc3700]",
      url: `https://www.reddit.com/submit?title=${encodeURIComponent(`⚽ Daily Football Quiz - ${score}/${total} ${emoji}`)}&url=${quizUrlEncoded}`,
    },
    {
      name: "Email",
      icon: () => <Mail size={18} />,
      color: "bg-muted hover:bg-muted/80",
      url: `mailto:?subject=${encodeURIComponent("Daily Football Quiz")}&body=${shareTextEncoded}`,
    },
  ];

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyFact = async () => {
    await navigator.clipboard.writeText(`⚽ Football Fact of the Day:\n${dailyFact}\n\n${QUIZ_URL}`);
    setFactCopied(true);
    setTimeout(() => setFactCopied(false), 2000);
  };

  return (
    <div className="text-center animate-fade-in max-w-md w-full">
      <div className="mb-4 flex items-center justify-center gap-2.5">
        <img src={footballLogo} alt="Daily Football Quiz" width={40} height={40} className="drop-shadow-[0_0_12px_hsl(30,100%,50%,0.3)]" loading="lazy" />
        <span className="font-heading text-lg font-bold tracking-tight text-foreground">
          Daily Football <span className="text-primary">Quiz</span>
        </span>
      </div>
      <div className="text-5xl mb-3">{emoji}</div>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
        {alreadyPlayed ? "You've already played today!" : message}
      </h2>
      <p className="text-muted-foreground text-lg mb-2">
        {alreadyPlayed ? "Your score today" : "You scored"}
      </p>
      <p className="font-heading text-6xl font-bold text-primary mb-4">
        {score}
        <span className="text-muted-foreground text-2xl font-normal">
          /{total}
        </span>
      </p>

      {streak > 0 && (
        <div className="flex items-center justify-center gap-2 mb-6 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 w-fit mx-auto">
          <Flame size={20} className="text-primary" />
          <span className="font-heading font-semibold text-foreground">
            {streak} day streak!
          </span>
        </div>
      )}

      {/* Share / Copy row */}
      <div className="flex gap-3 justify-center mb-4">
        <Button
          size="lg"
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-heading font-semibold gap-2"
          onClick={() => setShowPlatforms((v) => !v)}
        >
          <Share2 size={18} />
          Share
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="border-border font-heading font-semibold gap-2"
          onClick={handleCopy}
        >
          {copied ? <Check size={18} /> : <Copy size={18} />}
          {copied ? "Copied!" : "Copy"}
        </Button>
      </div>

      {/* Platform buttons */}
      {showPlatforms && (
        <div className="grid grid-cols-3 gap-2 mb-6 animate-fade-in">
          {platforms.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-white text-sm font-semibold transition-colors ${p.color}`}
            >
              {p.icon()}
              {p.name}
            </a>
          ))}
        </div>
      )}

      {/* Football Fact of the Day */}
      <div className="mt-6 mb-6 rounded-xl border border-border bg-card p-4 text-left">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb size={16} className="text-yellow-400 shrink-0" />
          <span className="font-heading text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Fact of the Day
          </span>
        </div>
        <p className="text-foreground text-sm leading-relaxed mb-3">
          {dailyFact}
        </p>
        <button
          onClick={handleCopyFact}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {factCopied ? <Check size={14} /> : <Copy size={14} />}
          {factCopied ? "Copied!" : "Copy fact"}
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
        <Clock size={16} />
        <span>
          Next quiz in{" "}
          <span className="font-heading font-semibold text-foreground">
            {String(hours).padStart(2, "0")}:{String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
          </span>
        </span>
      </div>
    </div>
  );
};

export default ResultScreen;
